// src/components/IntegrationAssignmentPanel.js
// Panel for managing integration assignments — allows ORG-scope RISK_MANAGER+ users
// to assign SELF-scoped CONTRIBUTOR+ users to manage integrations.

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { UserPlus, Trash2, Users, AlertCircle } from "react-feather";
import { useApi } from "../hooks/useApi";
import UserSelect from "./UserSelect";
import { formatUtcDate } from "../utils/dateTime";

const IntegrationAssignmentPanel = ({ organizationId }) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const apiCall = useApi();

  const fetchAssignments = useCallback(async () => {
    if (!organizationId) return;
    try {
      setLoading(true);
      const data = await apiCall("GET", "/integration-assignments");
      setAssignments(data || []);
    } catch (err) {
      setError("Failed to load integration assignments");
    } finally {
      setLoading(false);
    }
  }, [apiCall, organizationId]);

  const fetchEligibleUsers = useCallback(async () => {
    if (!organizationId) return;
    try {
      setLoadingUsers(true);
      const data = await apiCall("GET", "/integration-assignments/assignable-users");
      setEligibleUsers(data || []);
    } catch (err) {} finally {
      setLoadingUsers(false);
    }
  }, [apiCall, organizationId]);

  useEffect(() => {
    fetchAssignments();
    fetchEligibleUsers();
  }, [fetchAssignments, fetchEligibleUsers]);

  // Filter out already-assigned users (both real and pending invites) from the dropdown
  const availableUsers = useMemo(() => {
    const assignedUserIds = new Set(assignments.filter((a) => !a.isPending).map((a) => a.userId));
    const assignedInviteIds = new Set(assignments.filter((a) => a.isPending).map((a) => `invite:${a.userInviteId}`));
    return eligibleUsers.filter((u) => !assignedUserIds.has(u.id) && !assignedInviteIds.has(String(u.id)));
  }, [eligibleUsers, assignments]);

  const handleAssign = async () => {
    if (!selectedUserId) return;

    try {
      setAdding(true);
      setError(null);

      if (String(selectedUserId).startsWith("invite:")) {
        const userInviteId = parseInt(String(selectedUserId).replace("invite:", ""));
        if (!userInviteId || isNaN(userInviteId)) return;
        await apiCall("POST", "/integration-assignments", { userInviteId });
      } else {
        const userId = parseInt(selectedUserId);
        if (!userId || isNaN(userId)) return;
        await apiCall("POST", "/integration-assignments", { userId });
      }

      setSelectedUserId("");
      await fetchAssignments();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to assign user";
      setError(msg);
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (assignment) => {
    const removeKey = assignment.isPending ? `invite:${assignment.userInviteId}` : assignment.userId;
    try {
      setRemoving(removeKey);
      setError(null);
      if (assignment.isPending && assignment.userInviteId) {
        await apiCall("DELETE", `/integration-assignments/invite/${assignment.userInviteId}`);
      } else {
        await apiCall("DELETE", `/integration-assignments/${assignment.userId}`);
      }
      await fetchAssignments();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to remove assignment";
      setError(msg);
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-px flex-1 bg-gray-200" />
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest px-2">Integration Access</span>
        <div className="h-px flex-1 bg-gray-200" />
      </div>

      <div className="bg-white rounded-2xl border-2 border-gray-200 p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
            <Users className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Integration Assignments</h3>
            <p className="text-xs text-gray-500">
              Assign users to manage integrations.
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
            <p className="text-xs text-red-800">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto text-red-600 text-xs underline">Dismiss</button>
          </div>
        )}

        {/* Add user form */}
        <div className="flex items-center gap-2 mb-4">
          <UserSelect
            value={selectedUserId}
            onChange={setSelectedUserId}
            users={availableUsers}
            loading={loadingUsers}
            placeholder="-- Select a user to assign --"
            className="text-sm"
            onInviteSuccess={() => { fetchEligibleUsers(); }}
          />
          <button
            onClick={handleAssign}
            disabled={adding || !selectedUserId}
            className="flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-lg bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-600 hover:text-white hover:border-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <UserPlus className="w-3.5 h-3.5" />
            {adding ? "Assigning..." : "Assign"}
          </button>
        </div>

        {/* Assignments list */}
        {loading ? (
          <div className="text-center py-4">
            <p className="text-xs text-gray-400">Loading assignments...</p>
          </div>
        ) : assignments.length === 0 ? (
          <div className="text-center py-4 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500">No users assigned to manage integrations.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {assignments.map((a) => {
              const removeKey = a.isPending ? `invite:${a.userInviteId}` : a.userId;
              return (
                <div key={a.integrationAssignmentId} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {a.userFullName || `User #${a.userId}`}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {a.userEmail || ""}
                      {a.assignedAt && ` \u2022 Assigned ${formatUtcDate(a.assignedAt)}`}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemove(a)}
                    disabled={removing === removeKey}
                    className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50 transition-all disabled:opacity-50"
                  >
                    <Trash2 className="w-3 h-3" />
                    {removing === removeKey ? "Removing..." : "Remove"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default IntegrationAssignmentPanel;
